import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Compass, ArrowLeft, Send, Loader2, CheckCircle2, SkipForward } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const STEP_ICONS = ['', '1', '2', '3', '4'];

const MondayCompass = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { t } = useTranslation();
  const [boussole, setBoussole] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    startOrResumeBoussole();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startOrResumeBoussole = async () => {
    try {
      const res = await api.post('/api/boussole/start');
      const data = res.data;
      setBoussole(data.boussole);
      setMessages(data.boussole.conversations || [{ role: 'atlas', content: data.atlas_message, step: 1 }]);
      setIsComplete(data.boussole.statut === 'completee');
      if (data.boussole.statut !== 'completee') {
        setTimeout(() => inputRef.current?.focus(), 300);
      }
    } catch (err) {
      if (err.response?.status === 400) {
        // Already completed, fetch current silently
        try {
          const currentRes = await api.get('/api/boussole/current');
          if (currentRes.data.boussole) {
            setBoussole(currentRes.data.boussole);
            setMessages(currentRes.data.boussole.conversations || []);
            setIsComplete(true);
          }
        } catch (e) {
          console.error(e);
        }
      } else {
        console.error('Boussole start error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitStep = async () => {
    if (!input.trim() || sending || !boussole) return;

    const currentStep = boussole.current_step || 1;
    const userMsg = input.trim();
    setInput('');
    setSending(true);

    setMessages(prev => [...prev, { role: 'user', content: userMsg, step: currentStep }]);

    try {
      const res = await api.post('/api/boussole/step', {
        boussole_id: boussole.id,
        step: currentStep,
        response: userMsg
      });
      const data = res.data;
      setBoussole(data.boussole);
      setMessages(prev => [...prev, { role: 'atlas', content: data.atlas_message, step: currentStep }]);

      if (data.is_complete) {
        setIsComplete(true);
      } else {
        setTimeout(() => inputRef.current?.focus(), 300);
      }
    } catch (err) {
      toast.error(t('common.error'));
      setMessages(prev => prev.slice(0, -1));
      setInput(userMsg);
    } finally {
      setSending(false);
    }
  };

  const handleSkip = async () => {
    try {
      await api.put('/api/boussole/skip');
      toast.success(t('boussole.skipped'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(t('common.error'));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitStep();
    }
  };

  const getStepLabel = (step) => {
    const labels = {
      1: t('boussole.steps.emotion'),
      2: t('boussole.steps.kpi'),
      3: t('boussole.steps.priorities'),
      4: t('boussole.steps.decision')
    };
    return labels[step] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-screen bg-atlasBg">
        <div className="text-atlasCyan animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const currentStep = boussole?.current_step || 1;
  const progress = isComplete ? 100 : ((currentStep - 1) / 4) * 100;

  return (
    <div data-testid="monday-compass-page" className="flex flex-col h-screen bg-atlasBg">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-atlasBorder bg-atlasCard/80 backdrop-blur-lg">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                data-testid="boussole-back-btn"
                className="p-2 rounded-lg text-atlasTextSecondary hover:text-white hover:bg-white/5 transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Compass className="w-5 h-5 text-atlasCyan" />
                <h1 className="text-lg font-semibold text-white">{t('boussole.title')}</h1>
              </div>
            </div>
            {!isComplete && (
              <button
                onClick={handleSkip}
                data-testid="boussole-skip-btn"
                className="flex items-center gap-1.5 text-sm text-atlasTextSecondary hover:text-white transition-all px-3 py-1.5 rounded-lg hover:bg-white/5"
              >
                <SkipForward className="w-4 h-4" />
                {t('boussole.skip')}
              </button>
            )}
          </div>
          {/* Progress bar */}
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="flex-1 flex flex-col items-center gap-1">
                <div className={`h-1.5 w-full rounded-full transition-all duration-500 ${
                  (isComplete || currentStep > s) ? 'bg-atlasCyan' : currentStep === s ? 'bg-atlasCyan/50' : 'bg-atlasBorder'
                }`} />
                <span className={`text-xs transition-all ${
                  (isComplete || currentStep >= s) ? 'text-atlasCyan' : 'text-atlasTextSecondary'
                }`}>{getStepLabel(s)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((msg, i) => (
            <div
              key={i}
              data-testid={`boussole-msg-${i}`}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
                msg.role === 'user'
                  ? 'bg-atlasCyan/15 text-white rounded-br-md'
                  : 'bg-atlasCard border border-atlasBorder text-white rounded-bl-md'
              }`}>
                {msg.role === 'atlas' && (
                  <div className="flex items-center gap-2 mb-1.5">
                    <Compass className="w-3.5 h-3.5 text-atlasCyan" />
                    <span className="text-xs text-atlasCyan font-medium">ATLAS</span>
                  </div>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-atlasCard border border-atlasBorder rounded-2xl rounded-bl-md px-5 py-3.5">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-atlasCyan animate-spin" />
                  <span className="text-sm text-atlasTextSecondary">{t('boussole.thinking')}</span>
                </div>
              </div>
            </div>
          )}
          {isComplete && (
            <div className="flex justify-center pt-4">
              <div data-testid="boussole-complete-banner" className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-400">{t('boussole.completed')}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      {!isComplete && (
        <div className="flex-shrink-0 border-t border-atlasBorder bg-atlasCard/80 backdrop-blur-lg px-4 py-4">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <textarea
              ref={inputRef}
              data-testid="boussole-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('boussole.inputPlaceholder')}
              rows={1}
              className="flex-1 bg-atlasBg border border-atlasBorder rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-atlasCyan/50 placeholder:text-atlasTextSecondary"
            />
            <Button
              data-testid="boussole-send-btn"
              onClick={submitStep}
              disabled={!input.trim() || sending}
              className="btn-primary p-3 rounded-xl"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      )}

      {/* Back to dashboard if complete */}
      {isComplete && (
        <div className="flex-shrink-0 border-t border-atlasBorder bg-atlasCard/80 backdrop-blur-lg px-4 py-4">
          <div className="max-w-3xl mx-auto">
            <Button
              data-testid="boussole-back-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="btn-primary w-full"
            >
              {t('boussole.backToDashboard')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MondayCompass;
